import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { fetchStudentVideoUploads } from '../api';
import DownloadVideoButton from './DownloadVideoButton';

const Recordings = () => {
    const [recordings, setRecordings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openAlert, setOpenAlert] = useState(true);
    const { studentUsername } = useParams();

    const showError = (message) => {
        setError(message);
        setOpenAlert(true);
    };

    const exportToCsv = () => {
        const escape = (s) => `"${String(s).replace(/"/g, '""')}"`;
        const title = `Recordings for ${studentUsername}`;
        const header = 'Filename,Last Modified,Size';
        const rows = recordings.map(video => {
            const filename = video.key.split('/').pop();
            const lastModified = new Date(video.lastModified).toLocaleString('en-US');
            const size = (video.size / (1024 * 1024)).toFixed(2) + ' MB';
            return [filename, lastModified, size].map(escape).join(',');
        });
        const csv = [title, '', header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const today = new Date().toISOString().slice(0, 10);
        a.download = `${studentUsername}-recordings-${today}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        const getRecordings = async () => {
            setLoading(true);
            try {
                const data = await fetchStudentVideoUploads(studentUsername);
                setRecordings(data);
            } catch (err) {
                console.error(err);
                showError('Failed to fetch recordings.');
            } finally {
                setLoading(false);
            }
        };
        getRecordings();
    }, [studentUsername]);

    return (
        <div>
            <Link to={'/admin'} style={{ color: '#4447ad' }}>
                &larr; Back to dashboard
            </Link>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 2 }}>{studentUsername}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" color="textSecondary">
                    {recordings.length} recording{recordings.length !== 1 ? 's' : ''}
                </Typography>
                <Tooltip title="Export the list of recordings as CSV">
                    <span>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            onClick={exportToCsv}
                            disabled={recordings.length === 0}
                            sx={{ textTransform: 'none', color: '#4447ad', borderColor: 'rgba(68, 71, 173, 0.5)' }}
                        >
                            Export CSV
                        </Button>
                    </span>
                </Tooltip>
            </Box>
            {error && (
                <Collapse in={openAlert}>
                    <Alert 
                        severity="error" 
                        sx={{ maxWidth: '500px', mx: 'auto' }}
                        action={
                            <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={() => {
                                setOpenAlert(false);
                            }}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {error}
                    </Alert>
                </Collapse>
            )}
            {recordings.length === 0 && !error && !loading && <Typography>No recordings found.</Typography>}
            <List sx={{
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
                justifyContent: 'space-evenly',
            }}>
                { recordings.map( (video) => {
                    const path = video.key.split('/');
                    const filename = path[path.length - 1];
                    // Use the S3 upload timestamp (ISO string, parses in every browser incl. Safari)
                    // rather than the student-supplied filename; fall back to the filename if missing
                    const uploadedAt = new Date(video.lastModified);
                    const label = isNaN(uploadedAt.getTime())
                        ? filename
                        : `${uploadedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} \u00b7 ${uploadedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
                    const size = (video.size / (1024 * 1024)).toFixed(2) + ' MB';
                    return (
                        <ListItem 
                            key={video.key}
                            sx={{ maxWidth: '1200px'}}
                        >
                            <Card sx={{ 
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between', 
                                p: 1,
                                pt: {xs: 0, sm: 1}, 
                                width: '100%' 
                                }}>
                                <Box display="flex">
                                    <CardMedia
                                        component="img"
                                        sx={{ width: { xs: 30, sm: 60 }, objectFit: 'contain' }}
                                        image="/images/playback.png"
                                        alt="Playback"
                                    />
                                    <CardContent className='recordings-item' sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
                                        <Typography variant='subtitle2' sx={{ fontWeight: 500 }}>{size}</Typography>
                                    </CardContent>
                                </Box>
                                <CardActions sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <Tooltip title="View Video">
                                        <Link 
                                            to={encodeURIComponent(filename)} 
                                            state={{ key: video.key }} 
                                            style={{ textDecoration: 'none', color: '#4447ad', border: '1px solid rgba(68, 71, 173, 0.5)', borderRadius: '4px', padding: '4px 8px' }}
                                            aria-label="View Video"
                                        >
                                            <PlayCircleFilledIcon sx={{ fontSize: 30 }} />
                                        </Link>
                                    </Tooltip>
                                    <DownloadVideoButton
                                        studentUsername={studentUsername}
                                        videoKey={video.key}
                                        onError={showError}
                                    />
                                </CardActions>
                            </Card>
                        </ListItem>
                    )}
                )}
            </List>
        </div>
    )
}

export default Recordings;